import { useState, useRef } from "react";
import { analyzeTradingSignal } from "../services/tradingAnalysis";
import { DATABASE_API_URL, STOCK_SEARCH_API_URL, BACKEND_API_URL } from "../apiConfig";

export default function useStockRecommendModel() {
    const [state, setState] = useState({
        recommendations: [],
        isLoading: false,
        error: null,
        selectedStock: null,
        chartData: null,
        analysisTimestamp: null,
        metadata: null,
        page: 0,
        hasMore: true,
    });

    const isRequesting = useRef(false);

    const updateState = (updates) =>
        setState((prev) => ({ ...prev, ...updates }));

    const normalizeSymbol = (value) =>
        String(value || "").replace(/\.NS$/i, "").toUpperCase();

    const findBySymbol = (items, symbolWithSuffix) => {
        if (!Array.isArray(items)) return null;
        const normalizedTarget = normalizeSymbol(symbolWithSuffix);
        return items.find(item => normalizeSymbol(item?.symbol) === normalizedTarget);
    };

    const fetchUpstoxPrices = async (symbols) => {
        const response = await fetch(`${BACKEND_API_URL}/api/upstox/prices`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ symbols })
        });

        const result = await response.json();
        if (!response.ok) {
            throw new Error(result?.error || `Failed to fetch prices for ${symbols.join(', ')}`);
        }
        return result;
    };

    const fetchUpstoxHistorical = async (symbols) => {
        const response = await fetch(`${BACKEND_API_URL}/api/upstox/historical`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ symbols })
        });

        const result = await response.json();
        if (!response.ok) {
            throw new Error(result?.error || `Failed to fetch historical data for ${symbols.join(', ')}`);
        }
        return result;
    };

    const mergeByNormalizedSymbol = (items = [], newItems = []) => {
        const map = new Map(items.map(item => [normalizeSymbol(item?.symbol), item]));
        newItems.forEach(item => {
            if (item && item.symbol) {
                map.set(normalizeSymbol(item.symbol), item);
            }
        });
        return Array.from(map.values());
    };

    // Process a batch of stock data: fetch history & current price, run analysis
    const processStockDataBatch = async (stocks) => {
        try {
            const symbolsWithSuffix = stocks.map(s => `${s.symbol}.NS`);

            // Use Promise.all to fetch historical and price data concurrently for the entire batch
            const [historyResponse, priceResponse] = await Promise.all([
                fetch(`${BACKEND_API_URL}/api/upstox/historical`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ symbols: symbolsWithSuffix })
                }),
                fetch(`${BACKEND_API_URL}/api/upstox/prices`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ symbols: symbolsWithSuffix })
                })
            ]);

            if (!historyResponse.ok) {
                const err = await historyResponse.json();
                throw new Error(`Failed to fetch historical data: ${err.error || 'Unknown error'}`);
            }
            if (!priceResponse.ok) {
                const err = await priceResponse.json();
                throw new Error(`Failed to fetch current prices: ${err.error || 'Unknown error'}`);
            }

            const [historyResult, priceResult] = await Promise.all([
                historyResponse.json(),
                priceResponse.json()
            ]);

            let priceDataArray = Array.isArray(priceResult?.data) ? priceResult.data : [];
            let historyDataArray = Array.isArray(historyResult?.data) ? historyResult.data : [];

            const missingPriceSymbols = [];
            const missingHistorySymbols = [];

            for (const stock of stocks) {
                const symbolWithSuffix = `${stock.symbol}.NS`;
                const priceData = findBySymbol(priceDataArray, symbolWithSuffix);
                if (!priceData || priceData.price == null) {
                    missingPriceSymbols.push(symbolWithSuffix);
                }

                const historyItems = historyDataArray.filter(d => normalizeSymbol(d.symbol) === normalizeSymbol(symbolWithSuffix));
                if (historyItems.length === 0) {
                    missingHistorySymbols.push(symbolWithSuffix);
                }
            }

            if (missingPriceSymbols.length > 0) {
                console.warn(`[Recommendations] Re-fetching missing prices for ${missingPriceSymbols.join(", ")}`);
                try {
                    const fallbackPriceResult = await fetchUpstoxPrices(missingPriceSymbols);
                    priceDataArray = mergeByNormalizedSymbol(priceDataArray, Array.isArray(fallbackPriceResult?.data) ? fallbackPriceResult.data : []);
                } catch (fallbackErr) {
                    console.error(`[Recommendations] Fallback price retry failed:`, fallbackErr.message);
                }
            }

            if (missingHistorySymbols.length > 0) {
                console.warn(`[Recommendations] Re-fetching missing historical data for ${missingHistorySymbols.join(", ")}`);
                try {
                    const fallbackHistoryResult = await fetchUpstoxHistorical(missingHistorySymbols);
                    historyDataArray = mergeByNormalizedSymbol(historyDataArray, Array.isArray(fallbackHistoryResult?.data) ? fallbackHistoryResult.data : []);
                } catch (fallbackErr) {
                    console.error(`[Recommendations] Fallback history retry failed:`, fallbackErr.message);
                }
            }

            const results = stocks.map(stock => {
                const symbolWithSuffix = `${stock.symbol}.NS`;
                const priceData = findBySymbol(priceDataArray, symbolWithSuffix);
                if (!priceData || priceData.price == null) {
                    console.error(`No current price found for ${stock.symbol}`);
                    return null;
                }
                const currentPrice = priceData.price;

                const historyData = {
                    source: historyResult?.source,
                    data: Array.isArray(historyResult?.data)
                        ? historyResult.data.filter(d => normalizeSymbol(d.symbol) === normalizeSymbol(symbolWithSuffix))
                        : []
                };

                // Ensure data is sorted chronologically before analysis, mirroring stockChartModel
                const sortedData = historyData.data
                    .map(entry => {
                        const open = parseFloat(entry.open ?? entry.low);
                        const high = parseFloat(entry.high);
                        const low = parseFloat(entry.low);
                        const close = parseFloat(entry.close ?? entry.high);

                        // Validate that all values are numbers and make sense
                        if (isNaN(open) || isNaN(high) || isNaN(low) || isNaN(close)) {
                            return null;
                        }
                        // Validate price relationships
                        if (high < low || high < open || high < close || low > open || low > close) {
                            return null;
                        }

                        return {
                            timestamp: new Date(entry.date).getTime(),
                            open, high, low, close
                        };
                    })
                    .filter(item => item !== null) // Remove invalid data points
                    .sort((a, b) => a.timestamp - b.timestamp);

                if (sortedData.length === 0) {
                    console.error(`No processable historical data for ${stock.symbol}`);
                    return null;
                }

                const { signal, predictedBuyRange, predictedSellRange } =
                    analyzeTradingSignal(sortedData, currentPrice, stock.symbol);

                return {
                    symbol: stock.symbol,
                    name: stock.name,
                    currentPrice,
                    signal,
                    historicalData: historyData,
                    predictedBuyRange,
                    predictedSellRange
                };
            });

            return results.filter(r => r !== null);

        } catch (error) {
            console.error(`Error processing batch:`, error.message);
            return [];
        }
    };

    // Clear analysis on backend
    const clearAnalysisFromDatabase = async () => {
        try {
            await fetch(`${DATABASE_API_URL}/api/analysis/clear`, {
                method: 'POST',
            });
        } catch (error) {
            console.error('Failed to clear analysis, continuing...');
        }
    };

    // Fetch stock list, process all, and save to backend in smaller batches
    const fetchRecommendations = async (onProgress) => {
        if (isRequesting.current) return;
        isRequesting.current = true;

        updateState({ isLoading: true, error: null, recommendations: [] });

        try {
            await clearAnalysisFromDatabase();
            if (onProgress) onProgress(1);

            const searchResponse = await fetch(`${STOCK_SEARCH_API_URL}/search-top-stocks`);
            if (!searchResponse.ok) throw new Error('Failed to fetch stock list');

            const stocks = await searchResponse.json();
            if (!stocks || stocks.length === 0) throw new Error('No stocks available');

            const stocksToProcess = stocks;
           let displayedRecommendations = [];
            const localBatchSize = 20; // Use smaller batches to keep Yahoo/Upstox fallback reliable
            for (let i = 0; i < stocksToProcess.length; i += localBatchSize) {
                const batch = stocksToProcess.slice(i, i + localBatchSize);

                // Fetch and process stock data in parallel for the local batch
                const batchResults = await processStockDataBatch(batch);

                const validRecommendations = batchResults.filter(r => r.currentPrice > 0);

                // *** CHANGE: Save each batch to the database as it's processed ***
                if (validRecommendations.length > 0) {
                    const saveResponse = await fetch(`${DATABASE_API_URL}/api/analysis/save`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            recommendations: validRecommendations,
                            timestamp: new Date().toISOString()
                        })
                    });

                    if (!saveResponse.ok) {
                        // Log an error but continue processing other batches
                        console.error(`Failed to save batch starting at index ${i}. Status: ${saveResponse.status}`);
                    }
                }

                // Update progress after each local batch is processed and saved
                if (onProgress) {
                    const percent = Math.floor(((i + batch.length) / stocksToProcess.length) * 100);
                    onProgress(percent);
                }

                // Update state to show recommendations on the UI as they are processed
                displayedRecommendations = displayedRecommendations.concat(validRecommendations);
                updateState({
                    recommendations: [...displayedRecommendations],
                    analysisTimestamp: new Date().toISOString()
                });
            }

            if (onProgress) onProgress(100);
            updateState({ isLoading: false, error: null });

        } catch (error) {
            updateState({ error: error.message, isLoading: false, recommendations: [] });
        } finally {
            isRequesting.current = false;
        }
    };

    // Fetch the analysis from the backend, page by page
    const loadSavedAnalysis = async (reset = false, onProgress) => {
        if (state.isLoading || (!reset && !state.hasMore)) return;

        const nextPage = reset ? 1 : state.page + 1;
        updateState({ isLoading: true, error: null });
        if (onProgress) onProgress(10);

        try {
            const SAFE_LIMIT = 10; // or 20 if you want slightly more
const response = await fetch(`${DATABASE_API_URL}/api/analysis/db?page=${nextPage}&limit=${SAFE_LIMIT}`);
    if (onProgress) onProgress(50);

    if (!response.ok) throw new Error(`Failed to fetch analysis (page ${nextPage})`);

    const data = await response.json();

    if (!data.batches || data.batches.length === 0) throw new Error('No saved analysis found');

    // ✅ Flatten recommendations directly from batches
    const batchRecs = data.batches?.flatMap(batch => batch.recommendations || []) || [];

    const newRecommendations = reset
        ? batchRecs
        : [...state.recommendations, ...batchRecs];

    updateState({
        recommendations: newRecommendations,
        analysisTimestamp:(data.batches && data.batches.length > 0 ? data.batches[0].timestamp : null),
        metadata: data.batches?.[0]?.metadata || data.metadata,
        isLoading: false,
        error: null,
        page: nextPage,
        hasMore: nextPage < (data.metadata?.totalPages || 0),
    });

    if (onProgress) onProgress(100);

} catch (error) {
    console.error("Error fetching analysis:", error);
    updateState({
        ...state,
        isLoading: false,
        error: error.message,
    });
}



    };

    const viewStockData = (stock) => {
        updateState({ selectedStock: stock, chartData: stock.historicalData });
    };

    const clearSelectedStock = () => {
        updateState({ selectedStock: null, chartData: null });
    };

    return {
        state,
        viewStockData,
        clearSelectedStock,
        fetchRecommendations,
        loadSavedAnalysis
    };
}